/**
 * Decimal formatting and arithmetic for the client (SPEC.md §5.2, §10.2, §11.3).
 *
 * Money and quantity arrive from the API as **strings** and are parsed with `decimal.js`, never
 * with `parseFloat` — `18500.10` is not representable as a double, and a total built from a few
 * hundred of those stops matching the accountant's calculator.
 */
import Decimal from 'decimal.js'

/** The thousands separator SPEC.md §11.4 asks for: `1 240 500`, never `1,240,500`. */
const GROUP_SEPARATOR = ' '

/**
 * Group thousands with a space, without asking `Intl` what the separator should be.
 *
 * It used to ask. `Intl.NumberFormat('uz-UZ')` resolves the locale but hands back a **comma** as
 * the group separator in Chrome, and `formatMoney` then joins the fraction with a comma of its own
 * — so eighty thousand printed as `80,000,00`, which is unreadable and which a warehouse could
 * take for eight million. A separator the spec states outright is not one to delegate to locale
 * data that differs between engines.
 *
 * A plain space rather than a narrow no-break one, so a figure copied out of the app pastes as
 * something a spreadsheet accepts, and so the mono face's fixed advance keeps columns aligned.
 * Wrapping is prevented by `whitespace-nowrap` on the component instead.
 */
function group(value: bigint): string {
  const digits = value.toString()
  let out = ''
  for (let index = 0; index < digits.length; index += 1) {
    if (index > 0 && (digits.length - index) % 3 === 0) out += GROUP_SEPARATOR
    out += digits[index]
  }
  return out
}

/**
 * Parse a decimal string from the API.
 *
 * It throws on anything `decimal.js` cannot read, which is right for a value the server sent and
 * wrong for one a person is typing: a template that calls this on a field's raw text crashes the
 * render on `1,5` (audit U-01). Text from an input goes through `parseDecimalInput` first, and a
 * render path that must never fail uses `safeDecimal`.
 *
 * @example
 * toDecimal('18500.00').toString() // '18500'
 */
export function toDecimal(value: string | number | null | undefined): Decimal {
  if (value === null || value === undefined || value === '') {
    return new Decimal(0)
  }
  return new Decimal(String(value))
}

/** What a typed decimal must look like once its spaces are gone and its comma is a dot. */
const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/

/**
 * Every space a pasted figure can carry: ordinary, no-break (U+00A0), narrow no-break (U+202F) and
 * thin (U+2009). Spreadsheets and other apps group thousands with any of them.
 */
const ANY_SPACE = /[\s\u00A0\u202F\u2009]/g

/**
 * Turn what an operator typed into the plain decimal string the API accepts, or `null`.
 *
 * The app prints decimals with a comma (`84,5`), so a comma is what operators type back — and the
 * line editors used to hand `1,5` straight to `decimal.js`, which threw inside the render and froze
 * the screen (audit U-01). This is the single place that decides what a typed number means:
 *
 * - surrounding and grouping spaces are dropped, including the no-break ones a copied figure has;
 * - a comma is the decimal separator, exactly as the app prints it;
 * - the true minus sign `formatMoney` prints (U+2212) reads as `-`, so a figure copied out of the
 *   app pastes back in, and a leading `+` (how the API signs a change) is dropped;
 * - anything else — `2,`, `1.000,5`, `abc`, an empty field — is `null`, never a guess.
 *
 * @example
 * parseDecimalInput('1,5')         // '1.5'
 * parseDecimalInput(' 1 000,25 ')  // '1000.25'
 * parseDecimalInput('2,')          // null — still being typed
 * parseDecimalInput('')            // null
 */
export function parseDecimalInput(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null
  const normalised = raw
    .replace(ANY_SPACE, '')
    .replace(',', '.')
    .replace('\u2212', '-')
    .replace(/^\+/, '')
  return PLAIN_DECIMAL.test(normalised) ? normalised : null
}

/**
 * The value to store when an operator types into a quantity or price field.
 *
 * Normalised when it parses, so `1,5` is kept as `1.5` and every later computation and the payload
 * see one spelling. Kept exactly as typed when it does not, so a half-typed `2,` stays on screen
 * for the next keystroke instead of vanishing; the field then shows `validation.invalidNumber`
 * (see `isInvalidDecimalInput`) and the line counts as incomplete until it is fixed.
 *
 * @example
 * normaliseDecimalInput('1,5') // '1.5'
 * normaliseDecimalInput('2,')  // '2,'
 */
export function normaliseDecimalInput(raw: string): string {
  return parseDecimalInput(raw) ?? raw
}

/**
 * True when a field holds text that is not a number — the condition for its "Raqam noto‘g‘ri"
 * error. An empty field is not invalid here: whether it is *required* is the form's question.
 *
 * @example
 * isInvalidDecimalInput('2,')  // true
 * isInvalidDecimalInput('')    // false
 * isInvalidDecimalInput('1,5') // false
 */
export function isInvalidDecimalInput(raw: string | null | undefined): boolean {
  if (raw === null || raw === undefined || raw.trim() === '') return false
  return parseDecimalInput(raw) === null
}

/**
 * Parse for display, never throwing: anything unreadable counts as zero.
 *
 * For render paths — totals, hints, conversion lines — that run on every keystroke while a field
 * may hold `2,` or `-`. A render that throws takes the whole screen down with it (audit U-01); a
 * total that counts an unfinished field as zero is visibly incomplete, and the field beside it
 * says why. Business decisions (can this be posted?) must not lean on this: they check
 * `parseDecimalInput` and refuse instead.
 *
 * @example
 * safeDecimal('1,5').toString() // '1.5'
 * safeDecimal('2,').toString()  // '0'
 * safeDecimal(undefined)        // Decimal(0)
 */
export function safeDecimal(value: string | number | Decimal | null | undefined): Decimal {
  if (value === null || value === undefined) return new Decimal(0)
  if (value instanceof Decimal) return value.isFinite() ? value : new Decimal(0)
  if (typeof value === 'number') return Number.isFinite(value) ? new Decimal(value) : new Decimal(0)
  const parsed = parseDecimalInput(value)
  return parsed === null ? new Decimal(0) : new Decimal(parsed)
}

/**
 * True when `value` is a typed number that is zero or more — the guard for a price, where zero is
 * a legitimate answer (a free sample) but an empty field is not one.
 *
 * `Number('') >= 0` is `true`, which is how a return with an empty price used to be submitted as
 * `0.00` (audit U-21). This refuses the empty field explicitly.
 *
 * @example
 * isNonNegative('0')   // true
 * isNonNegative('')    // false
 * isNonNegative('-1')  // false
 */
export function isNonNegative(value: string | null | undefined): boolean {
  const parsed = parseDecimalInput(value)
  return parsed !== null && new Decimal(parsed).gte(0)
}

/**
 * True when `value` parses to a number greater than zero.
 *
 * The safe replacement for `Number(value) > 0` on a money or quantity field. §13 forbids
 * `parseFloat` on either, and the reason is not pedantry: the same habit that writes
 * `Number(amount) > 0` in a guard writes `Number(a) + Number(b)` in a total two months later, and
 * that one is wrong by a tenth of a tiyin per line.
 *
 * Unparseable input is `false`, not an exception. This runs on every keystroke of a form field,
 * where "18." and "" are things a person is in the middle of typing, not errors.
 *
 * @example
 * isPositive('0.001')  // true
 * isPositive('0')      // false
 * isPositive('18.')    // false — still being typed
 */
export function isPositive(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return false
  if (typeof value === 'number') return Number.isFinite(value) && value > 0
  // Through the input parser, so `1,5` typed into a quantity counts as the positive number it is.
  const parsed = parseDecimalInput(value)
  return parsed !== null && new Decimal(parsed).greaterThan(0)
}

/**
 * Format money for display: grouped thousands and a fixed number of decimals.
 *
 * @example
 * formatMoney('1240500.5') // '1 240 500,50'
 */
export function formatMoney(
  value: string | number | Decimal | null | undefined,
  decimals = 2,
): string {
  // `safeDecimal`, because a formatter is a render path: a half-typed field must not crash it.
  const parsed = safeDecimal(value).toFixed(decimals)
  const [whole = '0', fraction] = parsed.split('.')
  const grouped = group(BigInt(whole.replace('-', '')))
  const sign = parsed.startsWith('-') ? '−' : ''
  return fraction ? `${sign}${grouped},${fraction}` : `${sign}${grouped}`
}

/**
 * Format a quantity, trimming meaningless trailing zeros.
 *
 * A conversion factor of `12.000000` must read as `12`: the zeros are an artefact of
 * `NUMERIC(18,6)`, and showing them makes a box of twelve look like a measurement (SPEC.md §8.6).
 *
 * @example
 * formatQuantity('12.000000') // '12'
 * formatQuantity('84.500')    // '84,5'
 */
export function formatQuantity(value: string | number | Decimal | null | undefined): string {
  return trimmedNumber(safeDecimal(value).toFixed())
}

/**
 * `-1234.500` → `−1 234,5`: grouped, comma decimal, real minus, no trailing zeros.
 *
 * Shared by the quantity, percent and ratio formatters so the three can never disagree about the
 * separator — the dashboard used to print `1 240 500,00` beside `32.8%` (audit U-19).
 */
function trimmedNumber(plain: string): string {
  const [whole = '0', fraction] = plain.split('.')
  const trimmed = fraction?.replace(/0+$/, '') ?? ''
  const grouped = group(BigInt(whole.replace('-', '')))
  // `-0.04` rounded to one place is `-0.0`; a minus in front of a zero reads as a loss that is not.
  const isZero = grouped === '0' && !trimmed
  const sign = plain.startsWith('-') && !isZero ? '−' : ''
  return trimmed ? `${sign}${grouped},${trimmed}` : `${sign}${grouped}`
}

/** How many places a per-base unit cost is stored with (owner decision 7: `NUMERIC(18,6)`). */
export const UNIT_COST_DECIMALS = 6

/**
 * A per-base unit cost as a plain decimal string: rounded to six places, then trailing zeros
 * trimmed — but never below the two places money always shows.
 *
 * For an input field or a payload, where a comma or a group separator would not belong.
 *
 * @example
 * unitCostString('8.333333333') // '8.333333'
 * unitCostString('9000')        // '9000.00'
 */
export function unitCostString(value: string | number | Decimal | null | undefined): string {
  return safeDecimal(value)
    .toFixed(UNIT_COST_DECIMALS)
    .replace(/(\.\d\d\d*?)0+$/, '$1')
}

/**
 * Format a per-base unit cost for display: up to six places, grouped, comma decimal.
 *
 * Unit costs are stored with six places so that a box of twelve bought for 100,00 costs exactly
 * what was paid when every piece is counted back (owner decision 7). Printed with two places, that
 * 8,333333 read as 8,33 — and twelve of them as 99,96 — on the very screens used to check a
 * supplier's invoice. Totals stay two-place money; only the per-unit figure keeps its precision.
 * Zeros past the second place are trimmed, so a round price still reads like money.
 *
 * @example
 * formatUnitCost('8.333333')    // '8,333333'
 * formatUnitCost('9000.000000') // '9 000,00'
 * formatUnitCost('-0.5')        // '−0,50'
 */
export function formatUnitCost(value: string | number | Decimal | null | undefined): string {
  const plain = unitCostString(value)
  const [whole = '0', fraction = ''] = plain.split('.')
  const grouped = group(BigInt(whole.replace('-', '')))
  const sign = plain.startsWith('-') && !/^-0\.0+$/.test(plain) ? '−' : ''
  return `${sign}${grouped},${fraction}`
}

/**
 * Format a percentage that is already in percent units (`32.8` means 32,8 %).
 *
 * Rounded to `decimals`, then trailing zeros trimmed, with the same comma and minus sign as
 * money — so a margin reads `30%` rather than `30.0%`, and `-59.9` reads `−59,9%`. `signed` puts a
 * `+` on a rise, for a change against the previous period.
 *
 * @example
 * formatPercent('32.8')                     // '32,8%'
 * formatPercent('30.0')                     // '30%'
 * formatPercent('-59.94')                   // '−59,9%'
 * formatPercent('+12.4', { signed: true })  // '+12,4%'
 */
export function formatPercent(
  value: string | number | Decimal | null | undefined,
  options: { decimals?: number; signed?: boolean } = {},
): string {
  const rounded = safeDecimal(value).toFixed(options.decimals ?? 1)
  const plus = options.signed && new Decimal(rounded).greaterThan(0) ? '+' : ''
  return `${plus}${trimmedNumber(rounded)}%`
}

/**
 * Format a multiplier such as stock turnover: comma decimal, trimmed zeros, a `×` suffix.
 *
 * @example
 * formatRatio('0.00') // '0×'
 * formatRatio('1.25') // '1,25×'
 */
export function formatRatio(
  value: string | number | Decimal | null | undefined,
  decimals = 2,
): string {
  return `${trimmedNumber(safeDecimal(value).toFixed(decimals))}×`
}

/**
 * Render a base quantity in both forms, as SPEC.md §7.8.1 rule 5 requires:
 * `84 dona (7 quti)`, and with the remainder spelled out rather than rounded —
 * `17 dona (1 quti + 5 dona)`. Never `1.42 quti`: a partial box is a real physical thing and a
 * decimal box means nothing to a warehouse keeper.
 *
 * @example
 * describeInPackages('17', '12', 'quti', 'dona') // '17 dona (1 quti + 5 dona)'
 */
export function describeInPackages(
  baseQuantity: string | number,
  factor: string | number,
  packageName: string,
  baseName: string,
): string {
  const base = safeDecimal(baseQuantity)
  const perPackage = safeDecimal(factor)
  const plain = `${formatQuantity(base.toFixed())} ${baseName}`
  if (perPackage.lte(0)) {
    return plain
  }

  const packages = base.dividedToIntegerBy(perPackage)
  const remainder = base.minus(packages.times(perPackage))
  if (packages.isZero()) {
    return plain
  }

  const parts = [`${formatQuantity(packages.toFixed())} ${packageName}`]
  if (!remainder.isZero()) {
    parts.push(`${formatQuantity(remainder.toFixed())} ${baseName}`)
  }
  return `${plain} (${parts.join(' + ')})`
}
